import request from '@/utils/request';

/**
 * 船舶白名单规则管理API
 */

/**
 * 创建白名单规则
 * @param {Object} rule - 白名单规则对象
 * @param {number} rule.bridgeId - 桥梁ID
 * @param {string} rule.ruleName - 规则名称
 * @param {number} rule.priority - 优先级（1-5，1最高）
 * @param {number} rule.status - 状态：1-启用，0-禁用
 * @param {Object} rule.ruleConfig - 规则配置
 * @param {string} rule.ruleConfig.matchType - 匹配类型：VESSEL_TYPE（船舶类型）、MMSI_PATTERN（MMSI数字匹配）、NAME_PATTERN（船舶名称模式）
 * @param {string} rule.ruleConfig.description - 规则描述
 * @param {Array} rule.ruleConfig.vesselTypes - 船舶类型列表（当matchType为VESSEL_TYPE时）
 * @param {string} rule.ruleConfig.pattern - MMSI匹配模式（当matchType为MMSI_PATTERN时）
 * @param {Array} rule.ruleConfig.patterns - 船舶名称模式列表（当matchType为NAME_PATTERN时）
 * @param {boolean} rule.ruleConfig.caseSensitive - 是否大小写敏感（当matchType为NAME_PATTERN时）
 * @returns {Promise<{code: number, message: string, data: Object}>} 创建的白名单规则
 */
export function createWhitelistRule(rule) {
  return request({
    url: '/vessel/whitelist/rule',
    method: 'post',
    data: rule
  });
}

/**
 * 获取所有启用的白名单规则
 * @returns {Promise<{code: number, message: string, data: Array<Object>}>} 启用的白名单规则列表
 */
export function getAllActiveWhitelistRules() {
  return request({
    url: '/vessel/whitelist/rule/active',
    method: 'get'
  });
}

/**
 * 查询指定桥梁的白名单规则
 * @param {number} bridgeId - 桥梁ID
 * @param {number} [status] - 状态：1-启用，0-禁用，不传则查询全部
 * @returns {Promise<{code: number, message: string, data: Array<Object>}>} 桥梁的白名单规则列表
 */
export function getWhitelistRulesByBridgeId(bridgeId, status = null) {
  const params = {};
  if (status !== null) {
    params.status = status;
  }
  
  return request({
    url: `/vessel/whitelist/rule/bridge/${bridgeId}`,
    method: 'get',
    params
  });
}

/**
 * 根据ID查询白名单规则详情
 * @param {number} id - 规则ID
 * @returns {Promise<{code: number, message: string, data: Object}>} 白名单规则详情
 */
export function getWhitelistRuleById(id) {
  return request({
    url: `/vessel/whitelist/rule/${id}`,
    method: 'get'
  });
}

/**
 * 更新白名单规则
 * @param {number} id - 规则ID
 * @param {Object} rule - 白名单规则对象
 * @returns {Promise<{code: number, message: string, data: Object}>} 更新后的白名单规则
 */
export function updateWhitelistRule(id, rule) {
  return request({
    url: `/vessel/whitelist/rule/${id}`,
    method: 'put',
    data: rule
  });
}

/**
 * 删除白名单规则
 * @param {number} id - 规则ID
 * @returns {Promise<{code: number, message: string, data: null}>} 删除结果
 */
export function deleteWhitelistRule(id) {
  return request({
    url: `/vessel/whitelist/rule/${id}`,
    method: 'delete'
  });
}

/**
 * 更新规则状态
 * @param {number} id - 规则ID
 * @param {number} status - 状态：1-启用，0-禁用
 * @returns {Promise<{code: number, message: string, data: null}>} 更新结果
 */
export function updateWhitelistRuleStatus(id, status) {
  return request({
    url: `/vessel/whitelist/rule/${id}/status`,
    method: 'patch',
    params: { status }
  });
}

/**
 * 白名单规则匹配类型枚举
 */
export const WHITELIST_MATCH_TYPES = {
  VESSEL_TYPE: 'VESSEL_TYPE',      // 船舶类型匹配
  MMSI_PATTERN: 'MMSI_PATTERN',    // MMSI数字模式匹配
  NAME_PATTERN: 'NAME_PATTERN'     // 船舶名称模式匹配
};

/**
 * 白名单规则匹配类型选项
 */
export const WHITELIST_MATCH_TYPE_OPTIONS = [
  { value: 'VESSEL_TYPE', label: '船舶类型匹配' },
  { value: 'MMSI_PATTERN', label: 'MMSI数字匹配' },
  { value: 'NAME_PATTERN', label: '船舶名称匹配' }
];
